import React from 'react';
import { useStudioTypeSize } from '../StudioTypeScale';
import { StudioDiamondMark, StudioStarMark } from '../StudioMarks';
import type { StudioPageContent } from '../../../constants';
import LayoutFrame, { type LayoutContentProps } from './LayoutFrame';

interface CoverContentProps extends Partial<LayoutContentProps> {
  content: StudioPageContent;
  format: string;
}

const CoverContent: React.FC<CoverContentProps> = ({ content, format, tokens }) => {
  const isStory = format === 'story';
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const highlights = Array.isArray(content.bullets) ? content.bullets : [];

  return (
    <div>
      {content.kicker && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 12px',
            marginBottom: isStory ? 22 : 16,
            borderRadius: t.radius.pill,
            color: t.onAccent,
            background: t.accent,
            fontSize: typeSize(10),
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase'
          }}
        >
          <StudioStarMark size={typeSize(10.5)} />
          {content.kicker}
        </div>
      )}
      <div
        style={{
          maxWidth: 320,
          color: t.ink,
          fontSize: typeSize(isStory ? 39 : 30),
          fontWeight: 700,
          letterSpacing: '-1.4px',
          lineHeight: 1.08
        }}
      >
        {content.title}
      </div>
      {content.subtitle && (
        <div
          style={{
            maxWidth: 300,
            marginTop: isStory ? 18 : 13,
            paddingLeft: 13,
            borderLeft: `3px solid ${t.accent}`,
            color: t.muted,
            fontSize: typeSize(isStory ? 14 : 12.5),
            fontWeight: 500,
            lineHeight: 1.5
          }}
        >
          {content.subtitle}
        </div>
      )}
      {highlights.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: isStory ? 30 : 22
          }}
        >
          {highlights.map((highlight, index) => (
            <div
              key={`${index}-${highlight}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: isStory ? '7px 13px' : '6px 11px',
                borderRadius: t.radius.pill,
                background: t.chipBg,
                border: `1px solid ${t.hairline}`,
                color: t.ink,
                fontSize: typeSize(isStory ? 11.5 : 10.5),
                fontWeight: 600,
                lineHeight: 1.2
              }}
            >
              <StudioDiamondMark size={typeSize(11.5)} color={t.accent} />
              {highlight}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface CoverLayoutProps {
  width: number;
  height: number;
  format: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
}

const CoverLayout: React.FC<CoverLayoutProps> = props => (
  <LayoutFrame {...props}>
    <CoverContent content={props.content || {}} format={props.format} />
  </LayoutFrame>
);

export default CoverLayout;
